import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Menu from '@mui/material/Menu';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material/styles';
import Collapse from '@mui/material/Collapse';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { NAV } from './config-layout';
import Iconify from '../../theme/iconify/iconify';
import RouterLink from '../../theme/router/RouterLink';
import Scrollbar from '../../theme/scrollbar/Scrollbar';

import { usePathname } from '../../hooks/use-pathname';
import { useResponsive } from '../../hooks/use-responsive';

import ConfirmationModal from '../util/ConfirmationModal';
import { mislecAPI } from '../../util/axios';

function NavItem({ user, item, items, fetchChats, updateUser }) {
    const [openChatHistory, setOpenChatHistory] = React.useState(item.subpaths?.length > 0);
    const [modalState, setModalState] = React.useState(false);
    const [chatId, setChatId] = React.useState(null);
    const [menuAnchor, setMenuAnchor] = React.useState(null);
    const [editing, setEditing] = React.useState(false);
    const [title, setTitle] = React.useState('');
    const [chatSize, setChatSize] = React.useState(window.innerHeight - 56 - 16 - (items * 48) - 16);
    const menu = Boolean(menuAnchor);
    const [coursesSize, setCoursesSize] = React.useState(window.innerHeight - 56 - 16 - (items * 48) - 16 + 4);
    const [openCourses, setOpenCourses] = React.useState(user?.courses.length > 0);

    const { t } = useTranslation();
    const navigate = useNavigate();
    const pathname = usePathname();

    let active = item.path === pathname;

    const updateTitle = () => {
        if (!chatId) return;
        if (title === '') return;

        //  Timestamp
        const timestamp = new Date().toISOString().replace(/T/, ' ').replace(/\..+/, '');

        const data = {
            title: title,
            last_modified: timestamp
        }

        mislecAPI.put(`mislecAPI/api/chat/${chatId}/title`, data)
            .then(res => {
                setTimeout(() => {
                    setEditing(null);
                    setTitle('');
                }, 100);
                fetchChats();
            })
            .catch(err => {
                //  TODO
                //  Notify user of error
                console.log(err);
                if (err.response)
                    if (err.response.status === 401)
                        updateUser(null);
            });
    }

    const remove = () => {
        if (chatId) {
            mislecAPI.delete(`${process.env.REACT_APP_API}/api/chat/${chatId}`)
                .then(res => {
                    fetchChats();
                    setModalState(false);
                    setMenuAnchor(null);
                    if (pathname === `/chat/${chatId}`) navigate('/');
                    setChatId(null);
                })
                .catch(err => {
                    //  TODO
                    //  Notify user of error
                    console.log(err);
                    if (err.response)
                        if (err.response.status === 401)
                            updateUser(null);
                });
        }
    }

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            updateTitle();
        }
    }

    window.addEventListener('resize', () => {
        setChatSize(window.innerHeight - 56 - 16 - (items * 48) - 16);
    });

    React.useEffect(() => {
        setOpenChatHistory(item.subpaths?.length > 0);
    }, [item]);

    if (item.subpaths) {
        return (
            <>
                <ListItemButton
                    onClick={() => {
                        navigate('/chat');
                    }}
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        minHeight: 44,
                        maxHeight: 44,
                        borderRadius: 0.75,
                        typography: 'body2',
                        color: 'text.secondary',
                        textTransform: 'capitalize',
                        fontWeight: 'fontWeightMedium',
                        ...(active && {
                            color: 'primary.main',
                            fontWeight: 'fontWeightSemiBold',
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                            '&:hover': {
                                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
                            },
                        }),
                    }}
                >
                    <Grid container alignItems='center'>
                        <Iconify icon={item.icon} sx={{ mr: 1, alignSelf: 'center' }} />
                        <Box component='span' sx={{ alignSelf: 'center' }}>{t(`navigation.${item.title}`)}</Box>
                    </Grid>
                    {item.subpaths.length > 0 &&
                        <Grid item xs={2}>
                            <Grid container justifyContent='flex-end'
                                onClick={(event) => {
                                    event.stopPropagation()
                                    event.preventDefault()
                                    setOpenChatHistory(!openChatHistory)
                                }}
                            >
                                {openChatHistory ? (
                                    <Grid item xs={12}>
                                        <Tooltip title={t('navigation.tooltips.hideChat')} arrow>
                                            <IconButton disableRipple sx={{ marginRight: 'auto', p: 0 }}>
                                                <ExpandLessIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                ) : (
                                    <Grid item xs={12}>
                                        <Tooltip title={t('navigation.tooltips.showChat')} arrow>
                                            <IconButton disableRipple sx={{ marginRight: 'auto', p: 0 }}>
                                                <ExpandMoreIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                    }
                </ListItemButton>
                <Collapse in={openChatHistory}>
                    <Scrollbar
                        sx={{
                            height: 1,
                            maxHeight: `${chatSize}px`,
                            '& .simplebar-content': {
                                height: 1,
                                display: 'flex',
                                flexDirection: 'column',
                            },
                        }}
                    >
                        <Stack
                            component='nav'
                            spacing={0.5}
                            sx={{
                                pl: 0.5,
                                ml: 3,
                                borderLeft: (theme) => `solid 2px ${theme.palette.divider}`
                            }}
                        >
                            {item.subpaths.map((subitem) => (
                                <ListItemButton
                                    key={subitem.path}
                                    component={RouterLink}
                                    href={subitem.path}
                                    sx={{
                                        ml: 4,
                                        minHeight: 44,
                                        borderRadius: 0.75,
                                        typography: 'body2',
                                        color: 'text.secondary',
                                        textTransform: 'capitalize',
                                        fontWeight: 'fontWeightMedium',
                                        ...(subitem.path === pathname && {
                                            color: 'primary.main',
                                            fontWeight: 'fontWeightSemiBold',
                                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                            '&:hover': {
                                                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
                                            },
                                        }),
                                    }}
                                >
                                    <Grid container alignItems='center'>
                                        {editing && chatId === subitem._id ?
                                            <>
                                                <Grid item xs={8} sx={{ pr: 1.5 }}>
                                                    <input
                                                        id={`mISLec_edit_chat_title_${subitem._id}`}
                                                        type='text'
                                                        className='mISLec_edit_chat_title'
                                                        name='title'
                                                        value={title}
                                                        onClick={(e) => e.preventDefault()}
                                                        onChange={(e) => setTitle(e.target.value)}
                                                        onKeyDown={handleKeyDown}
                                                        style={{
                                                            color: '#637381 !important'
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <Grid container justifyContent='flex-end'>
                                                        <Grid item xs={6}>
                                                            <Tooltip title={t('navigation.tooltips.editChat')} arrow>
                                                                <IconButton
                                                                    disableRipple
                                                                    disableFocusRipple
                                                                    onClick={(event) => {
                                                                        event.preventDefault();
                                                                        updateTitle();
                                                                    }}
                                                                    sx={{ marginRight: 'auto', zIndex: 9, pr: 0, py: 0 }}>
                                                                    <Iconify icon='eva:checkmark-outline' />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Tooltip title={t('navigation.tooltips.cancelChatEdit')} arrow>
                                                                <IconButton
                                                                    disableRipple
                                                                    disableFocusRipple
                                                                    onClick={(event) => {
                                                                        event.preventDefault();
                                                                        setEditing(null);
                                                                        setTitle('');
                                                                    }}
                                                                    sx={{ marginRight: 'auto', zIndex: 9, pr: 0, py: 0, color: 'error.main' }}>
                                                                    <Iconify icon='eva:close-outline' />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </>
                                            :
                                            <>
                                                <Grid item xs={10}>
                                                    <Box component='span' className='mISLec_conversation_title'>{subitem.title}</Box>
                                                </Grid>
                                                <Grid item xs={2}>
                                                    <Grid container justifyContent='flex-end'>
                                                        <Grid item xs={12}>
                                                            <Tooltip title={t('navigation.tooltips.chatSettings')} arrow>
                                                                <IconButton
                                                                    disableRipple
                                                                    disableFocusRipple
                                                                    onClick={(event) => {
                                                                        event.preventDefault();
                                                                        setEditing(false);
                                                                        setTitle(subitem.title);
                                                                        setChatId(subitem._id);
                                                                        setMenuAnchor(event.currentTarget);
                                                                    }}
                                                                    sx={{ marginRight: 'auto', zIndex: 9, pr: 0, py: 0 }}>
                                                                    <MoreVertIcon fontSize='small' />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </>
                                        }
                                    </Grid>
                                    <Menu
                                        key={subitem._id}
                                        anchorEl={menuAnchor}
                                        open={menu && chatId === subitem._id}
                                        onClose={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            setMenuAnchor(null);
                                            setChatId(null);
                                        }}
                                    >
                                        <MenuItem
                                            onClick={(event) => {
                                                event.preventDefault();
                                                setEditing(true);
                                                setMenuAnchor(null);
                                                setTimeout(() => {
                                                    document.getElementById(`mISLec_edit_chat_title_${chatId}`).focus();
                                                }, 100);
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: '28px !important' }}>
                                                <Iconify icon='eva:edit-outline' />
                                            </ListItemIcon>
                                            <ListItemText sx={{ fontSize: '0.85rem' }}>
                                                {t('navigation.renameChat')}
                                            </ListItemText>
                                        </MenuItem>
                                        <MenuItem
                                            sx={{ color: 'error.main' }}
                                            onClick={(event) => {
                                                event.preventDefault();
                                                setModalState(true);
                                            }}
                                        >
                                            <ListItemIcon sx={{ color: 'error.main', minWidth: '28px !important' }}>
                                                <Iconify icon='eva:trash-outline' />
                                            </ListItemIcon>
                                            <ListItemText>
                                                {t('navigation.removeChat')}
                                            </ListItemText>
                                        </MenuItem>
                                    </Menu>
                                </ListItemButton>
                            ))}
                        </Stack>
                    </Scrollbar>
                </Collapse>
                <ConfirmationModal
                    type='remove'
                    title={t('navigation.removeChatModal.title')}
                    description={t('navigation.removeChatModal.description')}
                    confirmationText={t('navigation.removeChatModal.confirmation')}
                    confirm={() => remove()}
                    open={modalState}
                    onClose={() => setModalState(false)}
                />
            </>
        );
    } else if (item.subitems) {
        return (
            <>
                <ListItemButton
                    onClick={() => { navigate('/course') }}
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        minHeight: 44,
                        maxHeight: 44,
                        borderRadius: 0.75,
                        typography: 'body2',
                        color: 'text.secondary',
                        textTransform: 'capitalize',
                        fontWeight: 'fontWeightMedium',
                        ...(active && {
                            color: 'primary.main',
                            fontWeight: 'fontWeightSemiBold',
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                            '&:hover': {
                                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
                            },
                        }),
                    }}
                >
                    <Grid container alignItems='center'>
                        <Iconify icon={item.icon} sx={{ mr: 1, alignSelf: 'center' }} />
                        <Box component='span' sx={{ alignSelf: 'center' }}>{t(`navigation.${item.title}`)}</Box>
                    </Grid>
                    {user?.courses.filter(course => course.enrolled || user.role === 'admin').length > 0 &&
                        <Grid item xs={2}>
                            <Grid container justifyContent='flex-end'
                                onClick={(event) => {
                                    event.stopPropagation()
                                    event.preventDefault()
                                    setOpenCourses(!openCourses)
                                }}
                            >
                                {openCourses ? (
                                    <Grid item xs={12}>
                                        <Tooltip title={t('navigation.tooltips.hideCourse')} arrow>
                                            <IconButton disableRipple sx={{ marginRight: 'auto', p: 0 }}>
                                                <ExpandLessIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                ) : (
                                    <Grid item xs={12}>
                                        <Tooltip title={t('navigation.tooltips.showCourse')} arrow>
                                            <IconButton disableRipple sx={{ marginRight: 'auto', p: 0 }}>
                                                <ExpandMoreIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>}
                </ListItemButton>
                {user?.courses.length > 0 &&
                    <Collapse className='mISLec_navigation_courses_collapse' in={openCourses}>
                        <Scrollbar
                            sx={{
                                height: 1,
                                maxHeight: `${coursesSize}px`,
                                '& .simplebar-content': {
                                    height: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                },
                            }}
                        >
                            <Stack
                                component='nav'
                                spacing={0.5}
                                sx={{
                                    pl: 0.5,
                                    ml: 3,
                                    mt: '4px',
                                    borderLeft: (theme) => `solid 2px ${theme.palette.divider}`
                                }}
                            >
                                {item.subitems.map((subitem) => (
                                    <ListItemButton
                                        key={subitem.path}
                                        component={RouterLink}
                                        href={new RegExp(`^${subitem.path}(/|$)`).test(pathname) ? pathname : subitem.path}
                                        sx={{
                                            ml: 4,
                                            minHeight: 44,
                                            borderRadius: 0.75,
                                            typography: 'body2',
                                            color: 'text.secondary',
                                            textTransform: 'capitalize',
                                            fontWeight: 'fontWeightMedium',
                                            ...(new RegExp(`^${subitem.path}(/|$)`).test(pathname) && {
                                                color: 'primary.main',
                                                fontWeight: 'fontWeightSemiBold',
                                                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                                '&:hover': {
                                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
                                                },
                                            }),
                                        }}
                                    >
                                        <Grid container alignItems='center'>
                                            <Grid item xs={12}>
                                                <Box component='span' className='mISLec_conversation_title'>{subitem.title}</Box>
                                            </Grid>
                                        </Grid>
                                    </ListItemButton>
                                ))
                                }
                            </Stack>
                        </Scrollbar>
                    </Collapse>
                }
            </>
        );
    } else {
        return (
            <ListItemButton
                component={RouterLink}
                href={item.path}
                sx={{
                    minHeight: 44,
                    borderRadius: 0.75,
                    typography: 'body2',
                    color: 'text.secondary',
                    textTransform: 'capitalize',
                    fontWeight: 'fontWeightMedium',
                    ...(active && {
                        color: 'primary.main',
                        fontWeight: 'fontWeightSemiBold',
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                        '&:hover': {
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
                        },
                    }),
                }}
            >
                <Iconify icon={item.icon} sx={{ mr: 1 }} />
                <Box component='span'>{t(`navigation.${item.title}`)}</Box>
            </ListItemButton>
        );
    }
}

NavItem.propTypes = {
    item: PropTypes.object,
};

function Navigation({ user, updateUser, chats, fetchChats, courses, openNav, onCloseNav }) {
    const [fetched, setFetched] = React.useState(false);
    const [configuration, setConfiguration] = React.useState(null);

    const { t } = useTranslation();
    const navigate = useNavigate();
    const pathname = usePathname();
    const upLg = useResponsive('up', 'lg');

    React.useEffect(() => {
        if (openNav) {
            onCloseNav();
        }
    }, [pathname]);

    React.useEffect(() => {
        const subpaths = chats.map(chat => ({
            _id: chat._id,
            title: chat.title,
            path: `/chat/${chat._id}`
        }));

        const subitems = user.courses.filter(course => course.enrolled || user.role === 'admin').map((course) => {
            const foundCourse = courses.filter((currentCourse) => currentCourse._id === course.id);

            if (foundCourse.length > 0) {
                return ({
                    _id: course.id,
                    title: foundCourse[0].title,
                    path: `/course/${course.id}`
                })
            } else {
                return ({
                    _id: course.id,
                    title: '',
                    path: `/course/${course.id}`
                })
            }
        })

        const config = [{
            title: 'course',
            path: '/course',
            icon: 'mdi:book-outline',
            subitems: subitems
        }, /*         
        {
            title: 'document',
            path: '/documents',
            icon: 'ion:document-outline'
        },
        {
            title: 'corpus',
            path: '/corpus',
            icon: 'mdi:folder-outline'
        }
        */];

        if (user.role === 'admin') {
            config.push(
                {
                    title: 'users',
                    path: '/users',
                    icon: 'prime:users'
                },
                {
                    title: 'chat',
                    path: '/chat',
                    icon: 'fluent:chat-24-regular',
                    subpaths: subpaths
                }
            );
        }

        setConfiguration(config);
    }, [chats, courses, user?.courses]);

    if (!fetched) {
        if (!user) navigate('/login');
        setFetched(true);
    }

    const renderMenu = (
        <Stack component='nav' spacing={0.5} sx={{ px: 2, mt: 1 }}>
            {configuration && configuration.map((item) => (
                <NavItem key={item.title} user={user} item={item} items={configuration.length} fetchChats={fetchChats} updateUser={updateUser} />
            ))}
        </Stack>
    );

    const renderContent = (
        <>
            <Link to='/' style={{ textDecoration: 'none' }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px', marginBottom: '16px' }}>
                    <Tooltip title={t('header.home')} arrow>
                        <Button
                            variant={''}
                            onClick={() => {
                                navigate('/')
                            }}
                            sx={{
                                padding: 0
                            }}
                        >
                            <img
                                src={'/assets/images/mISLec-logo.png'}
                                alt={t('common.logo_alt')}
                                style={{ width: '75px', height: 'auto', maxWidth: '75px' }}
                            />
                        </Button>
                    </Tooltip>
                </div>
            </Link>

            <Divider sx={{ mt: 1, mx: 2 }} />

            {renderMenu}

            <Box sx={{ flexGrow: 1 }} />
        </>
    );

    return (
        <Box
            sx={{
                flexShrink: { lg: 0 },
                width: { lg: NAV.WIDTH },
            }}
        >
            {upLg ? (
                <Box
                    sx={{
                        height: 1,
                        position: 'fixed',
                        width: NAV.WIDTH,
                        borderRight: (theme) => `solid 1px ${theme.palette.divider}`,
                    }}
                >
                    {renderContent}
                </Box>
            ) : (
                <Drawer
                    open={openNav}
                    onClose={onCloseNav}
                    PaperProps={{
                        sx: {
                            width: NAV.WIDTH,
                        },
                    }}
                >
                    {renderContent}
                </Drawer>
            )}
        </Box>
    );
}

Navigation.propTypes = {
    openNav: PropTypes.bool,
    onCloseNav: PropTypes.func,
};

export default Navigation;